import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import selectedCases from '../data/selectedCases.json';

const AUTO_ADVANCE_MS = 8000;

export const SelectedCases = ({ vertical = 'mining' }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = selectedCases.length;
  const trackRef = useRef(null);

  const goTo = useCallback(
    (next) => setIndex(((next % total) + total) % total),
    [total]
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Auto-advance
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % total);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [paused, total]);

  // Keyboard navigation when section in focus
  useEffect(() => {
    const node = trackRef.current;
    if (!node) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    node.addEventListener('keydown', onKey);
    return () => node.removeEventListener('keydown', onKey);
  }, [next, prev]);

  return (
    <section
      className="selected-cases-section fade-in-section"
      data-testid="selected-cases-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="selected-cases-inner">
        <div className="selected-cases-header">
          <h2 className="selected-cases-title">Real work. Measurable outcomes.</h2>
          <p className="selected-cases-lede">
            A snapshot of recent cases across mining and industrial clients - from predictive
            maintenance and centrally governed data platforms to AI-assisted quoting and Copilot
            upskilling.
          </p>
        </div>

        <div
          className="selected-cases-carousel"
          ref={trackRef}
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label="Selected cases carousel"
          data-testid="selected-cases-carousel"
        >
          <div
            className="selected-cases-track"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {selectedCases.map((c, i) => {
              const detailHref = `/${vertical}/selected-cases/${c.slug}`;
              return (
                <div
                  className="selected-case-slide"
                  key={c.slug}
                  aria-hidden={i !== index}
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${total}: ${c.client}`}
                >
                  <Link
                    to={detailHref}
                    className="selected-case-row"
                    data-testid={`selected-case-${c.slug}`}
                    aria-label={`Read full case: ${c.client} – ${c.tagline}`}
                    tabIndex={i === index ? 0 : -1}
                  >
                    <div className="selected-case-media">
                      <img src={c.image} alt={`${c.client} – ${c.tagline}`} loading="lazy" />
                      <span className="selected-case-index">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>

                    <div className="selected-case-body">
                      <span className="selected-case-kicker">{c.kicker}</span>
                      <span className="selected-case-client">{c.client}</span>
                      <h3 className="selected-case-headline">{c.tagline}</h3>
                      <p className="selected-case-excerpt">{c.situation}</p>

                      <div className="selected-case-metrics">
                        {c.metrics.map((m, idx) => (
                          <div key={m.label} className="selected-case-metric">
                            <div className="selected-case-metric-value">{m.value}</div>
                            <div className="selected-case-metric-label">{m.label}</div>
                          </div>
                        ))}
                      </div>

                      <span className="selected-case-cta">
                        Read full case <ArrowRight size={16} />
                      </span>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* Controls — minimal dot navigation only */}
        <div className="selected-cases-controls">
          <div className="selected-cases-dots" role="tablist" aria-label="Select case">
            {selectedCases.map((c, i) => (
              <button
                key={c.slug}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Go to case ${i + 1}: ${c.client}`}
                onClick={() => goTo(i)}
                className={`selected-cases-dot ${i === index ? 'is-active' : ''}`}
                data-testid={`selected-cases-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SelectedCases;
